// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import * as Modal from '@app/features/app/components/dialogs/Modal';
import {PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import * as ChannelCommands from '@app/features/channel/commands/ChannelCommands';
import {DMCloseFailedModal} from '@app/features/channel/components/alerts/DMCloseFailedModal';
import {requestCopyMessageLink} from '@app/features/channel/components/MessageActionUtils';
import * as IARCommands from '@app/features/devtools/commands/IARCommands';
import {IARActionCards} from '@app/features/devtools/components/modals/IARActionCards';
import {
	getReportCategoryForReason,
	type IARPrimaryPath,
	type IARRuleReasonId,
} from '@app/features/devtools/components/modals/IARFlowUtils';
import styles from '@app/features/devtools/components/modals/IARModal.module.css';
import type {IARContext, IARStep} from '@app/features/devtools/components/modals/IARModal.types';
import {resolveIARModalContext} from '@app/features/devtools/components/modals/IARModalContext';
import {
	getIARActionCards,
	getIARChildSafetyRoutingNote,
	getIARGuidanceContent,
	getIARModalDescription,
	getIARPathPanelCopy,
	getIARPrimaryOptions,
	getIARProgressState,
	getIARReasonPanelCopy,
	getIARReportEligibilityCopy,
	getIARReviewContextCopy,
	getIARReviewHandlingCopy,
	getIARReviewPanelCopy,
	getIARRuleReasonOptions,
	getIARSpecialSafetyNote,
} from '@app/features/devtools/components/modals/IARModalCopy';
import {IARModalPreview} from '@app/features/devtools/components/modals/IARModalPreview';
import {useLeaveGuild} from '@app/features/guild/hooks/useLeaveGuild';
import {canSubmitReport, showReportRestrictionDialog} from '@app/features/moderation/utils/ReportVerificationGate';
import SelectedChannel from '@app/features/navigation/state/SelectedChannel';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as RelationshipActionUtils from '@app/features/relationship/utils/RelationshipActionUtils';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Textarea} from '@app/features/ui/components/form/Input';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import {UserSettingsModal} from '@app/features/user/components/modals/UserSettingsModal';
import {ME} from '@app/vendor/constants/AppConstants';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {AnimatePresence, motion} from 'framer-motion';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useMemo, useRef, useState} from 'react';

export type {IARContext} from '@app/features/devtools/components/modals/IARModal.types';

interface IARModalProps {
	context: IARContext;
}

const logger = new Logger('IARModal');
const STEP_ORDER: Record<IARStep, number> = {
	path: 0,
	reason: 1,
	guidance: 1,
	review: 2,
};
const slideVariants = {
	enter: (direction: number) => ({
		opacity: 0,
		x: direction > 0 ? 24 : -24,
	}),
	center: {
		opacity: 1,
		x: 0,
	},
	exit: (direction: number) => ({
		opacity: 0,
		x: direction > 0 ? -24 : 24,
	}),
};
const slideTransition = {
	type: 'spring' as const,
	stiffness: 500,
	damping: 40,
	mass: 0.7,
};
interface StepPaneProps {
	stepKey: IARStep;
	direction: number;
	children: React.ReactNode;
}
const StepPane: React.FC<StepPaneProps> = ({stepKey, direction, children}) => (
	<motion.div
		key={stepKey}
		className={styles.step}
		custom={direction}
		variants={slideVariants}
		initial="enter"
		animate="center"
		exit="exit"
		transition={slideTransition}
	>
		{children}
	</motion.div>
);
export const IARModal: React.FC<IARModalProps> = observer(({context}) => {
	const {i18n} = useLingui();
	const leaveGuild = useLeaveGuild();
	const [step, setStep] = useState<IARStep>('path');
	const [selectedPath, setSelectedPath] = useState<IARPrimaryPath | null>(null);
	const [selectedReason, setSelectedReason] = useState<IARRuleReasonId | null>(null);
	const [additionalInfo, setAdditionalInfo] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const previousStepRef = useRef<IARStep>('path');
	const direction = STEP_ORDER[step] - STEP_ORDER[previousStepRef.current];
	previousStepRef.current = step;
	const canSubmitPlatformReport = canSubmitReport();
	const resolvedContext = resolveIARModalContext(i18n, context);
	const primaryOptions = getIARPrimaryOptions(i18n, context, resolvedContext);
	const ruleReasonOptions = getIARRuleReasonOptions(i18n, context.type);
	const progressState = getIARProgressState(i18n, step, selectedPath);
	const safetyNote = getIARSpecialSafetyNote(i18n, step, selectedReason);
	const reportEligibilityCopy = getIARReportEligibilityCopy(i18n);
	const reviewHandlingCopy = getIARReviewHandlingCopy(i18n, resolvedContext);
	const reviewContextCopy = getIARReviewContextCopy(i18n);
	const actionCardsTitle = i18n._(msg`One-click actions`);
	const childSafetyRoutingNote = getIARChildSafetyRoutingNote(i18n, context.type, selectedReason);
	const modalDescription = getIARModalDescription(i18n);
	const selectedReasonOption = useMemo(() => {
		if (selectedReason === null) {
			return null;
		}
		return ruleReasonOptions.find((option) => option.value === selectedReason) ?? null;
	}, [ruleReasonOptions, selectedReason]);
	const reportCategory = useMemo(() => {
		if (selectedReason === null) {
			return null;
		}
		return getReportCategoryForReason(context.type, selectedReason);
	}, [context.type, selectedReason]);
	const closeModal = useCallback(() => {
		ModalCommands.pop();
	}, []);
	const handleOpenReportEligibility = useCallback(() => {
		showReportRestrictionDialog();
	}, []);
	const handleAdditionalInfoChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
		setAdditionalInfo(event.target.value);
	}, []);
	const openConnectionsSettings = useCallback(() => {
		ModalCommands.push(modal(() => <UserSettingsModal initialTab="privacy_safety" initialSubtab="connections" />));
	}, []);
	const openCommunicationSettings = useCallback(() => {
		ModalCommands.push(modal(() => <UserSettingsModal initialTab="privacy_safety" initialSubtab="communication" />));
	}, []);
	const handleBlockUser = useCallback(() => {
		if (resolvedContext.reportedUser === null) {
			return;
		}
		RelationshipActionUtils.showBlockUserConfirmation(i18n, resolvedContext.reportedUser);
	}, [i18n, resolvedContext.reportedUser]);
	const handleLeaveCommunity = useCallback(() => {
		if (resolvedContext.leaveableGuildId === null) {
			return;
		}
		leaveGuild(resolvedContext.leaveableGuildId);
	}, [leaveGuild, resolvedContext.leaveableGuildId]);
	const handleCloseDM = useCallback(() => {
		const dmChannel = resolvedContext.dmChannel;
		const dmDisplayName = resolvedContext.dmDisplayName;
		if (dmChannel === null) {
			return;
		}
		ModalCommands.push(
			modal(() => (
				<ConfirmModal
					title={i18n._(msg`Close DM`)}
					description={i18n._(
						msg`Close your current DM with ${dmDisplayName}. This does not block the user, and you can reopen the DM later.`,
					)}
					primaryText={i18n._(msg`Close DM`)}
					primaryVariant="danger-primary"
					onPrimary={async () => {
						try {
							await ChannelCommands.remove(dmChannel.id);
							const selectedChannel = SelectedChannel.selectedChannelIds.get(ME);
							if (selectedChannel === dmChannel.id) {
								RouterUtils.transitionTo(Routes.ME);
							}
							ToastCommands.createToast({
								type: 'success',
								children: i18n._(msg`DM closed`),
							});
						} catch (error) {
							logger.error('Failed to close DM:', error);
							window.setTimeout(() => {
								ModalCommands.push(modal(() => <DMCloseFailedModal />));
							}, 0);
						}
					}}
				/>
			)),
		);
	}, [i18n, resolvedContext.dmChannel, resolvedContext.dmDisplayName]);
	const handleCopyMessageLink = useCallback(() => {
		if (context.type !== 'message') {
			return;
		}
		requestCopyMessageLink(context.message, i18n);
	}, [context, i18n]);
	const actionCards = getIARActionCards(i18n, context, resolvedContext, {
		onBlockUser: handleBlockUser,
		onCloseDM: handleCloseDM,
		onCopyMessageLink: handleCopyMessageLink,
		onLeaveCommunity: handleLeaveCommunity,
		onOpenCommunicationSettings: openCommunicationSettings,
		onOpenConnectionsSettings: openConnectionsSettings,
	});
	const handleBack = useCallback(() => {
		switch (step) {
			case 'path':
				closeModal();
				return;
			case 'reason':
				setStep('path');
				return;
			case 'review':
				setStep('reason');
				return;
			case 'guidance':
				setStep('path');
				return;
		}
	}, [closeModal, step]);
	const handleContinue = useCallback(() => {
		switch (step) {
			case 'path':
				if (selectedPath === null) {
					ToastCommands.createToast({
						type: 'error',
						children: i18n._(msg`Pick an option to continue.`),
					});
					return;
				}
				if (selectedPath === 'platform') {
					if (!canSubmitPlatformReport) {
						showReportRestrictionDialog();
						return;
					}
					setStep('reason');
					return;
				}
				setStep('guidance');
				return;
			case 'reason':
				if (selectedReason === null) {
					ToastCommands.createToast({
						type: 'error',
						children: i18n._(msg`Pick the rule to review.`),
					});
					return;
				}
				setStep('review');
				return;
			case 'review':
			case 'guidance':
				return;
		}
	}, [canSubmitPlatformReport, i18n, selectedPath, selectedReason, step]);
	const handleSwitchToPlatformFlow = useCallback(() => {
		if (!canSubmitPlatformReport) {
			showReportRestrictionDialog();
			return;
		}
		setSelectedPath('platform');
		setStep('reason');
	}, [canSubmitPlatformReport]);
	const handleSubmit = useCallback(async () => {
		if (!canSubmitPlatformReport) {
			showReportRestrictionDialog();
			return;
		}
		if (selectedReason === null || reportCategory === null) {
			ToastCommands.createToast({
				type: 'error',
				children: i18n._(msg`Pick the rule to review.`),
			});
			return;
		}
		setSubmitting(true);
		try {
			switch (context.type) {
				case 'message':
					await IARCommands.reportMessage(
						context.message.channelId,
						context.message.id,
						reportCategory,
						additionalInfo,
					);
					break;
				case 'user':
					await IARCommands.reportUser(context.user.id, reportCategory, additionalInfo, context.guildId);
					break;
				case 'guild':
					await IARCommands.reportGuild(context.guild.id, reportCategory, additionalInfo);
					break;
			}
			ToastCommands.createToast({
				type: 'success',
				children: i18n._(msg`Report sent to ${PRODUCT_NAME}'s Safety Team.`),
			});
			closeModal();
		} catch (error) {
			logger.error('Failed to submit report:', error);
			ToastCommands.createToast({
				type: 'error',
				children: i18n._(msg`Couldn't submit. Try again.`),
			});
		} finally {
			setSubmitting(false);
		}
	}, [additionalInfo, canSubmitPlatformReport, closeModal, context, i18n, reportCategory, selectedReason]);
	const getDotClass = (index: number): string => {
		if (index < progressState.currentIndex) {
			return `${styles.progressDot} ${styles.progressDotComplete}`;
		}
		if (index === progressState.currentIndex) {
			return `${styles.progressDot} ${styles.progressDotActive}`;
		}
		return styles.progressDot;
	};
	const progressLabel = i18n._(
		msg`Step ${progressState.currentIndex + 1} of ${progressState.steps.length} · ${progressState.steps[progressState.currentIndex] ?? ''}`,
	);
	const renderPathStep = () => {
		const panelCopy = getIARPathPanelCopy(i18n);
		return (
			<StepPane stepKey="path" direction={direction}>
				<div className={styles.stepHeader}>
					<h2 className={styles.stepTitle}>{panelCopy.title}</h2>
					<p className={styles.stepBody}>{panelCopy.body}</p>
				</div>
				<IARModalPreview context={context} currentChannel={resolvedContext.currentChannel} />
				{!canSubmitPlatformReport && (
					<div className={`${styles.callout} ${styles.calloutWarn}`}>
						<div className={styles.calloutBody}>
							<div className={styles.calloutTitle}>{reportEligibilityCopy.title}</div>
							<div className={styles.calloutText}>{reportEligibilityCopy.body}</div>
							<div className={styles.inlineActions}>
								<Button variant="secondary" small fitContent onClick={handleOpenReportEligibility}>
									{i18n._(msg`Finish account setup`)}
								</Button>
							</div>
						</div>
					</div>
				)}
				<RadioGroup<IARPrimaryPath>
					options={primaryOptions}
					value={selectedPath}
					onChange={setSelectedPath}
					aria-label={i18n._(msg`Report routing`)}
				/>
			</StepPane>
		);
	};
	const renderReasonStep = () => {
		const panelCopy = getIARReasonPanelCopy(i18n);
		return (
			<StepPane stepKey="reason" direction={direction}>
				<div className={styles.stepHeader}>
					<h2 className={styles.stepTitle}>{panelCopy.title}</h2>
					<p className={styles.stepBody}>{panelCopy.body}</p>
				</div>
				<RadioGroup<IARRuleReasonId>
					options={ruleReasonOptions}
					value={selectedReason}
					onChange={setSelectedReason}
					aria-label={i18n._(msg`Platform report reason`)}
				/>
				{childSafetyRoutingNote !== null && (
					<div className={`${styles.callout} ${styles.calloutDanger}`}>
						<div className={styles.calloutBody}>
							<div className={styles.calloutTitle}>{childSafetyRoutingNote.title}</div>
							<div className={styles.calloutText}>{childSafetyRoutingNote.body}</div>
						</div>
					</div>
				)}
			</StepPane>
		);
	};
	const renderReviewStep = () => {
		const reviewPanelCopy = getIARReviewPanelCopy(i18n, selectedReasonOption);
		return (
			<StepPane stepKey="review" direction={direction}>
				<div className={styles.stepHeader}>
					<h2 className={styles.stepTitle}>{reviewPanelCopy.title}</h2>
					<p className={styles.stepBody}>{reviewPanelCopy.body}</p>
				</div>
				<IARModalPreview context={context} currentChannel={resolvedContext.currentChannel} />
				<div className={styles.callout}>
					<div className={styles.calloutBody}>
						<div className={styles.calloutTitle}>{reviewHandlingCopy.title}</div>
						<div className={styles.calloutText}>{reviewHandlingCopy.body}</div>
					</div>
				</div>
				{childSafetyRoutingNote !== null && (
					<div className={`${styles.callout} ${styles.calloutDanger}`}>
						<div className={styles.calloutBody}>
							<div className={styles.calloutTitle}>{childSafetyRoutingNote.title}</div>
							<div className={styles.calloutText}>{childSafetyRoutingNote.body}</div>
						</div>
					</div>
				)}
				{safetyNote !== null && (
					<div className={`${styles.callout} ${styles.calloutWarn}`}>
						<div className={styles.calloutBody}>
							<div className={styles.calloutTitle}>{i18n._(msg`Safety note`)}</div>
							<div className={styles.calloutText}>{safetyNote}</div>
						</div>
					</div>
				)}
				<div className={styles.contextSection}>
					<div className={styles.contextTitle}>{reviewContextCopy.title}</div>
					<div className={styles.contextDescription}>{reviewContextCopy.body}</div>
					<Textarea
						label={i18n._(msg`Additional details`)}
						value={additionalInfo}
						onChange={handleAdditionalInfoChange}
						placeholder={i18n._(msg`What happened, where, and why it breaks ${PRODUCT_NAME}'s rules.`)}
						minRows={3}
						maxRows={6}
						maxLength={1000}
						showCharacterCount={true}
						disabled={submitting}
					/>
				</div>
				<IARActionCards title={actionCardsTitle} cards={actionCards} />
			</StepPane>
		);
	};
	const renderGuidanceStep = () => {
		if (selectedPath === null || selectedPath === 'platform') {
			return null;
		}
		const guidanceContent = getIARGuidanceContent(i18n, selectedPath, context, resolvedContext);
		return (
			<StepPane stepKey="guidance" direction={direction}>
				<div className={styles.stepHeader}>
					<h2 className={styles.stepTitle}>{guidanceContent.title}</h2>
					<p className={styles.stepBody}>{guidanceContent.description}</p>
				</div>
				<IARActionCards title={actionCardsTitle} cards={actionCards} />
				<div className={styles.callout}>
					<div className={styles.calloutBody}>
						<div className={styles.calloutTitle}>{guidanceContent.noteTitle}</div>
						<div className={styles.calloutText}>{guidanceContent.noteBody}</div>
						<div className={styles.inlineActions}>
							<Button variant="secondary" small fitContent onClick={handleSwitchToPlatformFlow}>
								{guidanceContent.switchButtonLabel}
							</Button>
						</div>
					</div>
				</div>
			</StepPane>
		);
	};
	const renderStepContent = () => {
		switch (step) {
			case 'path':
				return renderPathStep();
			case 'reason':
				return renderReasonStep();
			case 'review':
				return renderReviewStep();
			case 'guidance':
				return renderGuidanceStep();
		}
	};
	const getSecondaryFooterLabel = (): string => {
		switch (step) {
			case 'path':
				return i18n._(msg`Cancel`);
			case 'reason':
			case 'review':
			case 'guidance':
				return i18n._(msg`Back`);
		}
	};
	const isContinueDisabled = (): boolean => {
		if (submitting) {
			return true;
		}
		switch (step) {
			case 'path':
				return selectedPath === null;
			case 'reason':
				return selectedReason === null;
			case 'review':
			case 'guidance':
				return false;
		}
	};
	const renderPrimaryFooterButton = () => {
		switch (step) {
			case 'path':
			case 'reason':
				return (
					<Button onClick={handleContinue} disabled={isContinueDisabled()}>
						{i18n._(msg`Continue`)}
					</Button>
				);
			case 'review':
				return (
					<Button onClick={handleSubmit} disabled={selectedReason === null || submitting} submitting={submitting}>
						{i18n._(msg`Submit report`)}
					</Button>
				);
			case 'guidance':
				return (
					<Button onClick={closeModal} disabled={submitting}>
						{i18n._(msg`Done`)}
					</Button>
				);
		}
	};
	return (
		<Modal.Root size="small" centered>
			<Modal.Header title={resolvedContext.title} />
			<Modal.Content>
				<Modal.ContentLayout>
					<Modal.ScreenReaderLabel text={modalDescription} />
					<div className={styles.progress} aria-hidden="true">
						<span className={styles.progressLabel}>{progressLabel}</span>
						<div className={styles.progressDots}>
							{progressState.steps.map((label, index) => (
								<span key={label} className={getDotClass(index)} />
							))}
						</div>
					</div>
					<div className={styles.stepContainer}>
						<AnimatePresence mode="wait" initial={false} custom={direction}>
							{renderStepContent()}
						</AnimatePresence>
					</div>
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer>
				<Button variant="secondary" onClick={handleBack} disabled={submitting}>
					{getSecondaryFooterLabel()}
				</Button>
				{renderPrimaryFooterButton()}
			</Modal.Footer>
		</Modal.Root>
	);
});
